import * as React from 'react';
import { connect } from 'react-redux';
import { isNil } from 'lodash';

import ActionButton from 'components/buttons/action-button/ActionButton';
import { withModal } from 'components/modal/ModalHOC';

import ConfirmModal from './ConfirmModal';

import { fetchTeamMembers, removeTeamMember } from 'modules/organizations/reducers/teamMembers';
import { addCollabarySupportToProfile } from 'modules/admin/reducers/admin-collabary-support/AdminCollabarySupport';

import { __t, applyTranslations } from 'utils/language';
import { isCollabaryCreatorSupport, isCollabarySupport } from 'utils/account';

import type { Account, InfluencerProfile, InfluencersAgency } from 'types';
import type AdminCollabarySupportType from 'modules/admin/reducers/admin-collabary-support/AdminCollabarySupport.type';

applyTranslations(require('./collabary-support.i18n.json'));

const ActionButtonWithModal = withModal(ActionButton, {
  modalContent: ConfirmModal,
  showClose: false
});

type OwnProps = {
  profile: InfluencerProfile | InfluencersAgency,
  profileType: AdminCollabarySupportType
};

type StateProps = {
  organizations: Array<Account> | undefined | null
};

type DispatchProps = {
  dispatchFetchMembers: () => void,
  dispatchAddSupport: () => Promise<void>,
  dispatchRemoveSupport: (a: number) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

const CollabarySupport = (
  {
    profile,
    profileType,
    dispatchAddSupport,
    dispatchRemoveSupport,
    dispatchFetchMembers,
    organizations
  }: Props
): JSX.Element => {
  React.useEffect(() => {
    if (profile.id) {
      dispatchFetchMembers();
    }
  }, [profile.id]);

  const isMembersLoading = isNil(organizations);
  const supportProfile = organizations?.find(
    account => (profileType === 'influencer'
      ? isCollabaryCreatorSupport(account)
      : isCollabarySupport(account))
  );
  const hasSupportProfileAlready = Boolean(supportProfile);
  const label = (hasSupportProfileAlready)
    ? __t('collabary_support.remove_label')
    : __t('collabary_support.add_label');

  return (
    <section>
      <ActionButtonWithModal
        disabled={isMembersLoading}
        modalContentProps={{
          onConfirm: async () => {
            if (hasSupportProfileAlready && supportProfile?.id) {
              await dispatchRemoveSupport(supportProfile.id);
            } else {
              await dispatchAddSupport();
            }
            dispatchFetchMembers();
          }
        }}
      >
        {label}
      </ActionButtonWithModal>
    </section>
  );
};

function mapStateToProps({ teamMembers }, ownProps: OwnProps): StateProps {
  return {
    organizations: teamMembers.mapByOrganizationId[ownProps?.profile?.organizationId]
  };
}

function mapDispatchToProps(dispatch, { profile, profileType }: OwnProps): DispatchProps {
  return {
    dispatchFetchMembers: () => dispatch(fetchTeamMembers(profile.organizationId)),
    dispatchAddSupport: () => dispatch(addCollabarySupportToProfile(profile.id, profileType)),
    dispatchRemoveSupport: (supportMemberId: number) => dispatch(removeTeamMember(profile.organizationId, supportMemberId))
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CollabarySupport);
